import { useState, useEffect, useRef } from "react";
import { X, Save, GripHorizontal, ImagePlus, FileText, Cable } from "lucide-react";
import { toast } from "sonner";
import api, { API, formatApiError, dlToken } from "@/lib/api";
import SpliceMap from "@/components/SpliceMap";
import AttachCablesDialog from "@/components/AttachCablesDialog";
import CameraView from "@/components/CameraView";
import { MODULES, FIBER_COLORS, fiberColorName } from "@/lib/modules";
import { useSettings } from "@/context/SettingsContext";
import { useI18n } from "@/context/I18nContext";
import { FieldRow } from "@/components/record/FieldRow";
import { OnuPicker } from "@/components/record/OnuPicker";
import { MicroductPanel } from "@/components/record/MicroductPanel";
import { GpsFields } from "@/components/record/GpsFields";
import ClientIdentityFields, { validateClient, composeClient } from "@/components/record/ClientIdentityFields";

function _hav(a, b) {
  const R = 6371000, r = Math.PI / 180;
  const dLat = (b[0] - a[0]) * r, dLng = (b[1] - a[1]) * r;
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(a[0] * r) * Math.cos(b[0] * r) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
}
function polyLen(geom) {
  let d = 0;
  for (let i = 1; i < geom.length; i++) d += _hav(geom[i - 1], geom[i]);
  return Math.round(d);
}

export default function RecordForm({ moduleKey, initial, presetCoords, presetGeometry, presetLength, mapCenter, onClose, onSaved, drawingActive, clientCableGeo, onRequestDrawCable, onExtendCable }) {
  const cfg = MODULES[moduleKey];
  const { settings } = useSettings();
  const { t } = useI18n();
  const isPoint = cfg.geo === "point";
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const drag = useRef(null);
  const terytLoadedRef = useRef(false);

  const onDragStart = (e) => {
    const startX = e.clientX, startY = e.clientY, base = { ...pos };
    drag.current = { startX, startY, base };
    const move = (ev) => {
      if (!drag.current) return;
      const maxX = Math.max(120, window.innerWidth / 2 - 80);
      const maxY = Math.max(120, window.innerHeight / 2 - 80);
      const clamp = (v, m) => Math.max(-m, Math.min(m, v));
      setPos({
        x: clamp(drag.current.base.x + (ev.clientX - drag.current.startX), maxX),
        y: clamp(drag.current.base.y + (ev.clientY - drag.current.startY), maxY),
      });
    };
    const up = () => { drag.current = null; window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };
  const [form, setForm] = useState(() => {
    const base = {};
    cfg.fields.forEach((f) => { base[f.name] = initial?.[f.name] ?? f.default ?? ""; });
    if (!initial?.id && moduleKey === "closures" && settings?.remember_last_closure) {
      try {
        const last = JSON.parse(localStorage.getItem("fp_last_closure") || "{}");
        cfg.fields.forEach((f) => {
          if (f.name !== "number" && last[f.name] != null && (base[f.name] === "" || base[f.name] == null)) base[f.name] = last[f.name];
        });
      } catch { /* ignore */ }
    }
    if (isPoint) {
      base.lat = presetCoords ? presetCoords[0] : (initial?.lat ?? "");
      base.lng = presetCoords ? presetCoords[1] : (initial?.lng ?? "");
    }
    if (presetLength != null && (base.length_m === "" || base.length_m == null)) base.length_m = presetLength;
    return base;
  });
  const [refs, setRefs] = useState({});
  const [busy, setBusy] = useState(false);
  const [attachData, setAttachData] = useState(null);
  const [splitterPorts, setSplitterPorts] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [onuList, setOnuList] = useState([]);
  const [lanMacBusy, setLanMacBusy] = useState(false);
  const [geometryOverride, setGeometryOverride] = useState(null);
  const [routedLen, setRoutedLen] = useState(0);
  const [allCables, setAllCables] = useState([]);
  const [selCables, setSelCables] = useState(() => new Set());
  const [assigning, setAssigning] = useState(false);
  const [passing, setPassing] = useState(null);
  const [tubeMap, setTubeMap] = useState({});
  const [nearbyCables, setNearbyCables] = useState([]);
  const isCatalog = !!cfg.catalog;
  const isSplice = moduleKey === "splices";
  const token = dlToken();

  useEffect(() => {
    const refFields = cfg.fields.filter((f) => f.type === "ref");
    Promise.all(refFields.map((f) => api.get(`/${f.ref}`).then((r) => [f.ref, r.data]).catch(() => [f.ref, []])))
      .then((pairs) => setRefs(Object.fromEntries(pairs)));
  }, [moduleKey]);

  useEffect(() => {
    if (moduleKey !== "cable_slack") return;
    const lat = presetCoords ? presetCoords[0] : (form.lat !== "" && form.lat != null ? Number(form.lat) : null);
    const lng = presetCoords ? presetCoords[1] : (form.lng !== "" && form.lng != null ? Number(form.lng) : null);
    if (lat == null || lng == null || Number.isNaN(lat) || Number.isNaN(lng)) return;
    api.get("/passing-at", { params: { lat, lng, radius: 30 } }).then((r) => {
      const cs = r.data.cables || [];
      setNearbyCables(cs);
      if (cs.length && !form.cable && cs[0].within) { set("cable", cs[0].id); set("cable_number", cs[0].number || ""); }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleKey, presetCoords]);

  const hasSplitterPort = cfg.fields.some((f) => f.name === "splitter_port");
  useEffect(() => {
    if (hasSplitterPort && form.splitter_id) {
      api.get(`/splitters/${form.splitter_id}/ports`).then((r) => setSplitterPorts(r.data.ports)).catch(() => setSplitterPorts(null));
    } else setSplitterPorts(null);
  }, [form.splitter_id, hasSplitterPort]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const tubeCount = Number(form.tube_count) || ((Number(form.fiber_count) && Number(form.fibers_per_tube)) ? Math.ceil(Number(form.fiber_count) / Number(form.fibers_per_tube)) : 0);

  // Auto-fill client's cable from the selected splitter (splitter is on a known cable)
  useEffect(() => {
    if (moduleKey === "clients" && form.splitter_id) {
      const sp = (refs.splitters || []).find((s) => s.id === form.splitter_id);
      if (sp && sp.cable_id) setForm((p) => ({ ...p, cable_id: sp.cable_id }));
    }
  }, [form.splitter_id, refs.splitters, moduleKey]);

  // Clients: list ONUs discovered by SNMP on the selected OLT (+ PON port) to assign one.
  useEffect(() => {
    if (moduleKey === "clients" && form.olt_id) {
      api.get("/snmp/discovered-onus", { params: { olt_id: form.olt_id, port: form.pon_port || "" } })
        .then((r) => setOnuList(r.data || [])).catch(() => setOnuList([]));
    } else setOnuList([]);
  }, [moduleKey, form.olt_id, form.pon_port]);

  // Clients: read MAC(s) of the client device plugged into the ONU Ethernet (UNI) port via SSH.
  const fetchOnuLanMac = async (o) => {
    if (!form.olt_id || !o || o.port == null || o.index == null) return;
    setLanMacBusy(true);
    try {
      const { data } = await api.get("/snmp/onu-lan-mac", { params: { olt_id: form.olt_id, port: o.port, index: o.index } });
      const macs = (data.macs || []).map((m) => (m.vlan ? `${m.mac} (VLAN ${m.vlan})` : m.mac)).join(", ");
      setForm((p) => ({ ...p, onu_lan_mac: macs }));
      if (!macs) toast.info(t("Brak MAC-ów na porcie Ethernet tego ONU (ONU offline lub brak podpiętego urządzenia)."));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || t("Nie udało się odczytać MAC z ONU"));
    } finally { setLanMacBusy(false); }
  };

  // Coverage: auto-generate identifier from SIMC + ULIC + building number
  useEffect(() => {
    if (moduleKey === "coverage") {
      const id = [form.simc, form.kod_ulic, form.nr_budynku]
        .map((x) => (x ?? "").toString().trim()).filter(Boolean).join("-");
      setForm((p) => (p.identyfikator === id ? p : { ...p, identyfikator: id }));
    }
  }, [form.simc, form.kod_ulic, form.nr_budynku, moduleKey]);

  const [ductOcc, setDuctOcc] = useState(null);
  const [ductPath, setDuctPath] = useState([]);
  const [ductCands, setDuctCands] = useState(null);
  const [showContinue, setShowContinue] = useState(false);
  // Microducts: load cables to show tube occupancy
  useEffect(() => {
    if (moduleKey === "microducts") api.get("/cables").then((r) => setAllCables(r.data || [])).catch(() => {});
  }, [moduleKey]);
  // Cables: live duct fill/occupancy + fit check
  useEffect(() => {
    if (moduleKey === "cables" && form.duct_id) {
      const cfgp = { params: {} };
      if (form.diameter_mm) cfgp.params.cable_diameter = form.diameter_mm;
      if (initial?.id) cfgp.params.exclude = initial.id;
      api.get(`/ducts/${form.duct_id}/occupancy`, cfgp).then((r) => setDuctOcc(r.data)).catch(() => setDuctOcc(null));
    } else setDuctOcc(null);
  }, [moduleKey, form.duct_id, form.diameter_mm, initial?.id]);

  // Wells / closures: what runs through this object
  useEffect(() => {
    if ((moduleKey === "wells" || moduleKey === "closures") && initial?.id) {
      api.get(`/passing/${moduleKey}/${initial.id}`).then((r) => setPassing(r.data)).catch(() => {});
    }
  }, [moduleKey, initial?.id]);

  // Microduct: bulk-assign selected cables to next free tubes
  const occTubes = () => new Set(allCables.filter((c) => c.microduct_id === initial?.id && c.microduct_tube).map((c) => Number(c.microduct_tube)));
  const freeTubesList = () => {
    const tc = Number(form.tube_count) || 0;
    const occ = occTubes();
    const r = [];
    for (let i = 1; i <= tc; i++) if (!occ.has(i)) r.push(i);
    return r;
  };
  const toggleCable = (cid) => {
    setSelCables((s) => {
      const n = new Set(s);
      if (n.has(cid)) {
        n.delete(cid);
        setTubeMap((m) => { const mm = { ...m }; delete mm[cid]; return mm; });
      } else {
        n.add(cid);
        setTubeMap((m) => {
          const used = new Set(Object.values(m).map((x) => Number(x)).filter(Boolean));
          const free = freeTubesList().filter((x) => !used.has(x));
          return { ...m, [cid]: free.length ? free[0] : "" };
        });
      }
      return n;
    });
  };
  const assignCables = async () => {
    const ids = [...selCables];
    if (!ids.length || !initial?.id) return;
    setAssigning(true);
    try {
      const assignments = ids.map((cid) => ({ cable_id: cid, tube: tubeMap[cid] === "" || tubeMap[cid] == null ? null : Number(tubeMap[cid]) }));
      const { data } = await api.post(`/microducts/${initial.id}/assign-cables`, { assignments });
      toast.success(`${t("Przypisano kabli")}: ${data.assigned.length}` + (data.total > 0 ? ` · ${t("wolne mikrorury")}: ${data.free_left}` : ""));
      const r = await api.get("/cables");
      setAllCables(r.data || []);
      setSelCables(new Set());
      setTubeMap({});
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setAssigning(false); }
  };

  // Cables: auto-route along the selected microduct / pipeline geometry
  const autoRoute = () => {
    const src = form.microduct_id
      ? (refs.microducts || []).find((x) => x.id === form.microduct_id)
      : form.pipeline_id
        ? (refs.pipelines || []).find((x) => x.id === form.pipeline_id)
        : (refs.ducts || []).find((x) => x.id === form.duct_id);
    const geom = src?.geometry;
    if (!geom || geom.length < 2) { toast.error(t("Wybrana trasa nie ma zapisanej geometrii na mapie")); return; }
    const len = polyLen(geom);
    setGeometryOverride(geom);
    setRoutedLen(len);
    setForm((p) => ({ ...p, length_m: len }));
    toast.success(`${t("Ustawiono geometrię kabla wzdłuż trasy")} (${geom.length} pkt, ${len} m)`);
    setShowContinue(true);
  };

  const loadDuctNext = async () => {
    try {
      const { data } = await api.post("/ducts/next", { duct_ids: ductPath.map((d) => d.id) });
      setDuctCands(data.candidates || []);
    } catch { setDuctCands([]); }
  };
  const addDuctSeg = (d) => { setDuctPath((p) => [...p, d]); setDuctCands(null); };
  const loadDuctNextOrFirst = async () => {
    if (!ductPath.length) setDuctCands((refs.ducts || []).map((d) => ({ id: d.id, name: d.name })));
    else await loadDuctNext();
  };
  const [histOpen, setHistOpen] = useState(false);
  const [histRows, setHistRows] = useState([]);
  const toggleHist = async () => {
    if (!histOpen) {
      try { const { data } = await api.get(`/cables/${initial.id}/geo-history`); setHistRows(data.history || []); } catch { setHistRows([]); }
    }
    setHistOpen((v) => !v);
  };
  const restoreGeo = async (index) => {
    if (!window.confirm(t("Przywrócić tę wersję trasy kabla?"))) return;
    try {
      const { data } = await api.post(`/cables/${initial.id}/restore-geometry`, { index });
      toast.success(`${t("Przywrócono wersję — długość")}: ${data.length_m} m`);
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || t("Błąd przywracania")); }
  };
  const undoGeometry = async () => {
    if (!window.confirm(t("Cofnąć ostatnią zmianę trasy tego kabla?"))) return;
    try {
      const { data } = await api.post(`/cables/${initial.id}/undo-geometry`);
      toast.success(`${t("Cofnięto zmianę trasy — długość")}: ${data.length_m} m`);
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || t("Błąd cofania")); }
  };
  const extendViaDucts = async (end) => {
    if (!ductPath.length) { toast.error(t("Najpierw wybierz odcinki kanalizacji")); return; }
    try {
      const { data } = await api.post("/ducts/stitch", { duct_ids: ductPath.map((d) => d.id) });
      const pts = data.geometry || [];
      if (pts.length < 2) { toast.error(t("Brak geometrii odcinków")); return; }
      let breakLinks = false;
      try {
        const { data: sum } = await api.get(`/cables/${initial.id}/link-summary`);
        if (sum.items?.length) {
          if (!window.confirm(`${t("Uwaga — przedłużenie kabla może naruszyć istniejące połączenia:")}\n\n• ${sum.items.join("\n• ")}\n\n${t("Kontynuować?")}`)) return;
          breakLinks = window.confirm(`${t("Czy usunąć te powiązania (spawy/połączenia)?")}\n\nOK = ${t("usuń powiązania")}\n${t("Anuluj")} = ${t("zachowaj (tylko przedłuż)")}`);
        }
      } catch { /* brak podsumowania nie blokuje */ }
      const res = await api.post(`/cables/${initial.id}/extend`, { end, points: pts, duct_ids: ductPath.map((d) => d.id), break_links: breakLinks });
      toast.success(`${t("Przedłużono kabel po kanalizacji — nowa długość")}: ${res.data.length_m} m${res.data.broken?.length ? " · " + t("usunięto") + ": " + res.data.broken.join(", ") : ""}`);
      setDuctPath([]); setDuctCands(null);
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || t("Błąd przedłużania")); }
  };
  const buildDuctRoute = async () => {
    if (!ductPath.length) return;
    try {
      const { data } = await api.post("/ducts/stitch", { duct_ids: ductPath.map((d) => d.id) });
      if (!data.geometry || data.geometry.length < 2) { toast.error(t("Brak geometrii odcinków")); return; }
      setGeometryOverride(data.geometry); setRoutedLen(data.length_m);
      setForm((p) => ({ ...p, length_m: data.length_m, duct_ids: ductPath.map((d) => d.id), duct_id: ductPath[0].id }));
      toast.success(`${t("Zbudowano trasę")}: ${data.count} odc. · ${data.length_m} m`);
      setShowContinue(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Poland bounding box (approx): lat 49–55°N, lng 14–24.2°E
  const PL = { latMin: 49.0, latMax: 55.0, lngMin: 14.0, lngMax: 24.2 };
  const latN = form.lat === "" || form.lat == null ? null : Number(form.lat);
  const lngN = form.lng === "" || form.lng == null ? null : Number(form.lng);
  const outOfBounds = isPoint && latN != null && lngN != null && !Number.isNaN(latN) && !Number.isNaN(lngN) &&
    (latN < PL.latMin || latN > PL.latMax || lngN < PL.lngMin || lngN > PL.lngMax);

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error(t("Brak dostępu do lokalizacji"));
    navigator.geolocation.getCurrentPosition(
      (p) => { set("lat", p.coords.latitude); set("lng", p.coords.longitude); toast.success(t("Pobrano lokalizację GPS")); },
      () => toast.error(t("Nie udało się pobrać lokalizacji"))
    );
  };

  const houseCoords = () => presetCoords || (form.lat != null && form.lat !== "" && form.lng != null && form.lng !== "" ? [Number(form.lat), Number(form.lng)] : null);
  const save = async () => {
    if (moduleKey === "clients" && form.splitter_id && !clientCableGeo && !initial?.id && onRequestDrawCable) { toast.error(t("Najpierw narysuj trasę kabla do splitera (po słupach)")); return; }
    const isLegacyClient = !!initial?.id && !initial?.pesel && !initial?.nip;
    let clientExtra = {};
    if (moduleKey === "clients") {
      const cErr = validateClient(form, { legacy: isLegacyClient });
      if (cErr) { toast.error(t(cErr)); return; }
      if (terytLoadedRef.current && !isLegacyClient && form.city && !form.teryt_simc) {
        toast.error(t("Wybierz miejscowość z listy słownika TERYT")); return;
      }
      clientExtra = composeClient(form, { name: initial?.name, address: initial?.address });
    }
    const missing = cfg.fields.find((f) => f.required && !f.custom && (form[f.name] === "" || form[f.name] == null));
    if (missing) { toast.error(`${t("Pole")} „${missing.label}" ${t("jest wymagane")}`); return; }
    if (outOfBounds && !window.confirm("Współrzędne znajdują się poza obszarem Polski (49–55°N, 14–24°E). Zapisać mimo to?")) return;
    setBusy(true);
    try {
      const payload = { ...form, ...clientExtra };
      delete payload._postal_suggested;
      cfg.fields.forEach((f) => {
        if (f.type === "number" && payload[f.name] !== "") payload[f.name] = Number(payload[f.name]);
      });
      if (isPoint) {
        payload.lat = payload.lat === "" || payload.lat == null ? null : Number(payload.lat);
        payload.lng = payload.lng === "" || payload.lng == null ? null : Number(payload.lng);
      }
      if (presetGeometry) payload.geometry = presetGeometry;
      if (geometryOverride) payload.geometry = geometryOverride;
      let res;
      if (initial?.id) res = await api.put(`/${moduleKey}/${initial.id}`, payload);
      else res = await api.post(`/${moduleKey}`, payload);
      if (!initial?.id && moduleKey === "closures" && settings?.remember_last_closure) {
        try {
          const keep = {};
          cfg.fields.forEach((f) => { if (f.name !== "number") keep[f.name] = form[f.name]; });
          localStorage.setItem("fp_last_closure", JSON.stringify(keep));
        } catch { /* ignore */ }
      }
      if (isCatalog && imageFile && res.data?.id) {
        const fd = new FormData();
        fd.append("entity_type", moduleKey);
        fd.append("entity_id", res.data.id);
        fd.append("file", imageFile);
        try { await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); } catch {}
      }
      // Auto-wire a splitter's input fiber: mark it occupied + create a feeding splice.
      try {
        const id = res.data?.id;
        if (id && moduleKey === "splitters" && payload.input_cable_id && payload.input_fiber_number) {
          await api.post(`/splitters/${id}/wire-input`);
          toast.success("Podłączono włókno wejściowe splittera (auto-spaw)");
        }
        if (moduleKey === "splices" && payload.target_type === "Splitter" &&
            payload.target_splitter_id && payload.cable_id && payload.fiber_in) {
          await api.post(`/splitters/${payload.target_splitter_id}/wire-input`, null,
            { params: { cable_id: payload.cable_id, fiber: Number(payload.fiber_in) } });
          toast.success(t("Splitter zasilony wybranym włóknem"));
        }
        if (id && moduleKey === "clients" && payload.splitter_id) {
          const { data } = await api.post(`/clients/${id}/ensure-client-cable`, clientCableGeo ? { geometry: clientCableGeo.geometry } : {});
          if (data?.created) toast.success(t("Utworzono kabel kliencki do splittera"));
        }
      } catch { /* wiring is best-effort */ }
      toast.success(t("Zapisano pomyślnie"));
      if (moduleKey === "closures" && !initial?.id && res.data?.cable_candidates?.length) {
        setAttachData({ id: res.data.id, number: res.data.number, candidates: res.data.cable_candidates, saved: res.data });
        setBusy(false);
        return;
      }
      onSaved(res.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (drawingActive) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 p-4" data-testid="record-form-overlay">
      <div className="bg-slate-800 border border-slate-700 rounded-lg w-full max-w-lg max-h-[85vh] flex flex-col shadow-2xl"
        style={{ transform: `translate(${pos.x}px, ${pos.y}px)` }} data-testid="record-form-modal">
        <div onMouseDown={onDragStart} className="flex items-center justify-between px-5 py-3 border-b border-slate-700 cursor-move select-none" data-testid="record-form-header">
          <h3 className="font-heading font-bold text-lg flex items-center gap-2">
            <GripHorizontal size={16} className="text-slate-500" />
            {initial?.id ? t("Edytuj") : t("Dodaj")}: {t(cfg.singular)}
          </h3>
          <button onClick={onClose} data-testid="record-form-close" className="text-slate-400 hover:text-white">
            <X size={20} />
          </button>
        </div>
        <div className="overflow-y-auto p-5 grid grid-cols-2 gap-3">
          {presetGeometry && (
            <div className="col-span-2 text-xs text-cyan-400 bg-cyan-900/20 border border-cyan-800/40 rounded px-3 py-2" data-testid="draw-geometry-info">
              Narysowano trasę: {presetGeometry.length} punktów · długość {presetLength} m
            </div>
          )}
          {moduleKey === "clients" && form.splitter_id && !initial?.id && onRequestDrawCable && (
            <div className="col-span-2" data-testid="client-cable-route">
              {clientCableGeo ? (
                <div className="bg-green-900/20 border border-green-800/40 text-green-300 flex items-center justify-between gap-2 rounded px-3 py-2 text-xs">
                  <span>✓ {t("Trasa kabla narysowana")}: {clientCableGeo.geometry.length} pkt · {clientCableGeo.length} m</span>
                  <button type="button" onClick={() => onRequestDrawCable && onRequestDrawCable({ splitterId: form.splitter_id, house: houseCoords() })} className="underline text-green-200 shrink-0">{t("Narysuj ponownie")}</button>
                </div>
              ) : (
                <div className="bg-amber-900/20 border border-amber-800/40 text-amber-300 rounded px-3 py-2 text-xs">
                  <div className="mb-1.5">{t("Poprowadź kabel kliencki od domu po słupach do splitera. Wybrany spliter miga na mapie.")}</div>
                  <button type="button" onClick={() => onRequestDrawCable && onRequestDrawCable({ splitterId: form.splitter_id, house: houseCoords() })} data-testid="client-cable-draw-btn"
                    className="bg-amber-600 hover:bg-amber-500 text-white rounded px-3 py-1.5 font-medium">{t("Narysuj trasę kabla (po słupach)")}</button>
                </div>
              )}
            </div>
          )}
          {moduleKey === "clients" && (
            <ClientIdentityFields form={form} set={set} setForm={setForm} initial={initial} t={t}
              onTerytStatus={(v) => { terytLoadedRef.current = v; }} />
          )}
          {moduleKey === "cable_slack" && nearbyCables.length > 0 && (
            <div className="col-span-2 rounded-md bg-cyan-900/15 border border-cyan-800/40 px-3 py-2" data-testid="slack-nearby-suggest">
              <div className="text-[11px] uppercase tracking-wide text-cyan-300 font-semibold mb-1.5">{t("Najbliższe kable")} ({t("klik = wybór")})</div>
              <div className="flex flex-col gap-1 max-h-40 overflow-y-auto">
                {nearbyCables.map((c) => (
                  <button type="button" key={c.id} data-testid={`slack-nearby-${c.id}`}
                    onClick={() => { set("cable", c.id); set("cable_number", c.number || ""); }}
                    className={`flex items-center gap-2 text-sm px-2 py-1 rounded ${form.cable === c.id ? "bg-cyan-500/25 text-white" : "hover:bg-slate-800 text-slate-200"}`}>
                    <Cable size={13} className={c.within ? "text-rose-400" : "text-slate-500"} />
                    <span className="font-medium">{c.number || c.id}</span>
                    {c.dist_m != null && <span className={`text-[11px] ${c.within ? "text-cyan-400" : "text-slate-500"}`}>{c.dist_m} m</span>}
                    {c.free != null && <span className="ml-auto text-[11px] text-slate-400">{c.free}/{c.fiber_count} {t("wolnych")}</span>}
                  </button>
                ))}
              </div>
              {!nearbyCables[0]?.within && (
                <div className="text-[10px] text-amber-400/80 mt-1">{t("Brak kabla dokładnie w punkcie — pokazano najbliższe. Kliknij bliżej trasy kabla.")}</div>
              )}
            </div>
          )}
          {cfg.fields.filter((f) => !f.custom).map((f) => (
            <FieldRow key={f.name} f={f} form={form} set={set} refs={refs}
              splitterPorts={splitterPorts} initial={initial} isSplice={isSplice} />
          ))}
          {moduleKey === "clients" && form.olt_id && (
            <OnuPicker form={form} setForm={setForm} onuList={onuList} fetchOnuLanMac={fetchOnuLanMac} lanMacBusy={lanMacBusy} />
          )}
          {moduleKey === "cables" && (form.microduct_id || form.pipeline_id || form.duct_id) && (
            <div className="col-span-2 rounded-md bg-amber-900/15 border border-amber-800/40 px-3 py-2 text-xs" data-testid="cable-autoroute">
              <div className="flex items-center justify-between gap-2">
                <span className="text-amber-300">{t("Trasuj kabel wzdłuż wybranej infrastruktury")}</span>
                <button type="button" data-testid="cable-autoroute-btn" onClick={autoRoute}
                  className="px-2.5 py-1 rounded bg-amber-600 hover:bg-amber-500 text-slate-900 font-semibold">{t("Trasuj")}</button>
              </div>
              {geometryOverride && <div className="mt-1 text-cyan-300 font-mono" data-testid="cable-autoroute-result">{t("Ustawiono trasę")}: {geometryOverride.length} pkt · {routedLen} m</div>}
              {showContinue && (
                <div className="mt-1.5 rounded bg-cyan-900/20 border border-cyan-800/40 px-2 py-1.5" data-testid="cable-continue-prompt">
                  <div className="text-cyan-300 mb-1 font-medium">{t("Prowadzić kabel dalej?")}</div>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" data-testid="continue-duct" onClick={() => { setShowContinue(false); if (!ductPath.length && form.duct_id) { setDuctPath([{ id: form.duct_id, name: (refs.ducts || []).find((d) => d.id === form.duct_id)?.name || t("odcinek 1") }]); loadDuctNext(); } else { loadDuctNextOrFirst(); } }}
                      className="px-2 py-1 rounded bg-slate-700 hover:bg-slate-600 text-slate-100">{t("Kolejny odcinek kanalizacji")}</button>
                    {onRequestDrawCable && <button type="button" data-testid="continue-draw" onClick={() => { setShowContinue(false); onRequestDrawCable(form, geometryOverride); }}
                      className="px-2 py-1 rounded bg-slate-700 hover:bg-slate-600 text-slate-100">{t("Rysuj dalej (po słupach / dowolnie)")}</button>}
                    <button type="button" data-testid="continue-no" onClick={() => setShowContinue(false)} className="px-2 py-1 text-slate-400 underline">{t("Nie, zakończ")}</button>
                  </div>
                </div>
              )}
            </div>
          )}
          {moduleKey === "cables" && initial?.id && (initial.geometry?.length >= 2) && onExtendCable && (
            <div className="col-span-2 rounded-md border border-cyan-800/40 bg-cyan-900/10 px-3 py-2 text-xs" data-testid="cable-extend">
              <div className="text-cyan-300 mb-1.5">{t("Przedłuż trasę kabla (bez wstawiania mufy)")}</div>
              <div className="flex gap-2">
                <button type="button" data-testid="cable-extend-start" onClick={() => onExtendCable(initial, "start")} className="px-2.5 py-1 rounded bg-cyan-700 hover:bg-cyan-600 text-white">{t("Przedłuż od początku")}</button>
                <button type="button" data-testid="cable-extend-end" onClick={() => onExtendCable(initial, "end")} className="px-2.5 py-1 rounded bg-cyan-700 hover:bg-cyan-600 text-white">{t("Przedłuż od końca")}</button>
                {initial.geo_history?.length > 0 && (
                  <button type="button" data-testid="cable-undo-geo" onClick={undoGeometry} className="px-2.5 py-1 rounded bg-slate-700 hover:bg-slate-600 text-slate-100">{t("Cofnij ostatnią zmianę trasy")} ({initial.geo_history.length})</button>
                )}
                {initial.geo_history?.length > 0 && (
                  <button type="button" data-testid="cable-hist-toggle" onClick={toggleHist} className="px-2.5 py-1 rounded bg-slate-700 hover:bg-slate-600 text-slate-100">{t("Historia trasy")} ({initial.geo_history.length})</button>
                )}
              </div>
              {histOpen && (
                <div className="mt-2 rounded border border-slate-700 bg-slate-900/60 max-h-40 overflow-auto" data-testid="cable-hist-list">
                  {histRows.length === 0 ? <div className="px-2 py-2 text-[11px] text-slate-500">{t("Brak zapisanych wersji")}</div> : histRows.slice().reverse().map((h) => (
                    <div key={h.index} className="flex items-center justify-between px-2 py-1 border-b border-slate-800 text-[11px]">
                      <span className="text-slate-300">{h.ts ? new Date(h.ts).toLocaleString("pl-PL") : "—"} · <span className="font-mono">{h.length_m} m</span> · {h.points} pkt</span>
                      <button type="button" data-testid={`cable-hist-restore-${h.index}`} onClick={() => restoreGeo(h.index)} className="px-2 py-0.5 rounded bg-cyan-700 hover:bg-cyan-600 text-white">{t("Przywróć")}</button>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-slate-500 mt-1">{t("Doklikasz kolejne punkty na mapie (po słupach lub dowolnie). Jeśli kabel jest podłączony do mufy/spawów — pojawi się ostrzeżenie.")}</p>
            </div>
          )}
          {moduleKey === "cables" && form.duct_id && ductOcc && (
            <div className="col-span-2 rounded-md border border-slate-700 bg-slate-800/40 px-3 py-2 text-xs" data-testid="cable-duct-occupancy">
              <div className="flex items-center justify-between">
                <span className="text-slate-300">{t("Zajętość rury")}{ductOcc.inner_diameter_mm ? ` (światło ${ductOcc.inner_diameter_mm} mm)` : ""}</span>
                <span className="font-mono text-slate-200">{ductOcc.count} {t("kabli")}{ductOcc.fill_pct != null ? ` · ${ductOcc.fill_pct}%` : ""}</span>
              </div>
              {ductOcc.fill_pct != null && (
                <div className="mt-1 h-2 rounded bg-slate-700 overflow-hidden">
                  <div className="h-full" style={{ width: `${Math.min(100, ductOcc.fill_pct)}%`, background: ductOcc.fill_pct > 100 ? "#dc2626" : ductOcc.fill_pct > 60 ? "#f59e0b" : "#22c55e" }} />
                </div>
              )}
              {form.diameter_mm && ductOcc.too_thick && <div className="mt-1 text-red-400" data-testid="duct-too-thick">⚠ {t("Kabel za gruby na tę rurę")} ({form.diameter_mm} mm &gt; {ductOcc.inner_diameter_mm} mm)</div>}
              {form.diameter_mm && !ductOcc.too_thick && ductOcc.fill_pct_with_candidate != null && (
                <div className={`mt-1 ${ductOcc.fits ? "text-emerald-400" : "text-red-400"}`} data-testid="duct-fit">
                  {ductOcc.fits ? "✓ " : "⚠ "}{t("Po dodaniu")}: {ductOcc.fill_pct_with_candidate}%{!ductOcc.fits ? ` — ${t("brak miejsca")}` : ""}
                </div>
              )}
              {!ductOcc.inner_diameter_mm && <div className="mt-1 text-amber-400">{t("Uzupełnij światło rury w kanalizacji, aby liczyć zajętość.")}</div>}
            </div>
          )}
          {moduleKey === "cables" && (
            <div className="col-span-2 rounded-md border border-violet-800/50 bg-violet-900/10 px-3 py-2 text-xs" data-testid="cable-duct-path">
              <div className="flex items-center justify-between">
                <span className="text-violet-300 font-medium">{t("Trasa przez kanalizację (wielosegmentowa)")}</span>
                {ductPath.length > 0 && <button type="button" onClick={() => { setDuctPath([]); setDuctCands(null); }} className="text-slate-400 underline" data-testid="duct-path-clear">{t("Wyczyść")}</button>}
              </div>
              {ductPath.length > 0 && (
                <ol className="mt-1 space-y-0.5">
                  {ductPath.map((d, i) => (
                    <li key={d.id} className="flex items-center gap-2" data-testid={`duct-path-seg-${i}`}>
                      <span className="w-4 h-4 rounded-full bg-violet-600 text-white text-[10px] flex items-center justify-center shrink-0">{i + 1}</span>
                      <span className="text-slate-200 flex-1 truncate">{d.name || d.id}</span>
                      {i === ductPath.length - 1 && <button type="button" onClick={() => setDuctPath((p) => p.slice(0, -1))} className="text-red-400">✕</button>}
                    </li>
                  ))}
                </ol>
              )}
              <div className="mt-1.5 flex gap-2">
                <button type="button" data-testid="duct-path-add" onClick={() => { if (!ductPath.length) { setDuctCands((refs.ducts || []).map((d) => ({ id: d.id, name: d.name }))); } else { loadDuctNext(); } }}
                  className="px-2 py-1 rounded bg-slate-700 hover:bg-slate-600 text-slate-100">{ductPath.length ? t("Dodaj odcinek / odgałęzienie") : t("Wybierz pierwszy odcinek")}</button>
                {ductPath.length > 0 && <button type="button" data-testid="duct-path-build" onClick={buildDuctRoute} className="px-2 py-1 rounded bg-violet-600 hover:bg-violet-500 text-white font-semibold">{t("Zbuduj trasę kabla")}</button>}
              </div>
              {ductCands && (
                <div className="mt-1.5 border-t border-violet-800/40 pt-1.5" data-testid="duct-path-candidates">
                  <div className="text-slate-400 mb-1">{ductPath.length ? t("Od której studni skręcić — wybierz kanalizację:") : t("Wybierz kanalizację:")}</div>
                  {ductCands.length === 0 && <div className="text-amber-400">{t("Brak połączonych odcinków w pobliżu.")}</div>}
                  <div className="flex flex-wrap gap-1">
                    {ductCands.map((c) => (
                      <button type="button" key={c.id} data-testid={`duct-cand-${c.id}`} onClick={() => addDuctSeg(c)}
                        className="px-2 py-0.5 rounded bg-slate-800 hover:bg-violet-700 text-slate-200 border border-slate-700">{c.name || c.id}{c.dist_m != null ? ` · ${c.dist_m}m` : ""}</button>
                    ))}
                  </div>
                </div>
              )}
              {form.duct_ids && form.duct_ids.length > 1 && <div className="mt-1 text-cyan-300" data-testid="duct-path-built">{t("Trasa z odcinków")}: {form.duct_ids.length}</div>}
              {initial?.id && (initial.geometry?.length >= 2) && ductPath.length > 0 && (
                <div className="mt-2 border-t border-violet-800/40 pt-1.5 flex flex-wrap items-center gap-2" data-testid="duct-extend">
                  <span className="text-violet-300 w-full">{t("Dołącz te odcinki do istniejącej trasy kabla:")}</span>
                  <button type="button" data-testid="duct-extend-start" onClick={() => extendViaDucts("start")} className="px-2 py-1 rounded bg-cyan-700 hover:bg-cyan-600 text-white">{t("od początku")}</button>
                  <button type="button" data-testid="duct-extend-end" onClick={() => extendViaDucts("end")} className="px-2 py-1 rounded bg-cyan-700 hover:bg-cyan-600 text-white">{t("od końca")}</button>
                </div>
              )}
            </div>
          )}
          {moduleKey === "microducts" && Number(form.tube_count) > 0 && (
            <MicroductPanel form={form} initial={initial} allCables={allCables} selCables={selCables}
              toggleCable={toggleCable} tubeMap={tubeMap} setTubeMap={setTubeMap}
              freeTubesList={freeTubesList} assignCables={assignCables} assigning={assigning} />
          )}
          {moduleKey === "cables" && tubeCount > 1 && (
            <div className="col-span-2 rounded-md bg-slate-800/40 border border-slate-700 px-3 py-2" data-testid="cable-tube-colors">
              <div className="text-xs text-slate-300 mb-2 flex items-center gap-1.5"><Cable size={13} className="text-cyan-400" /> {t("Kolory tub")} ({tubeCount})</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {Array.from({ length: tubeCount }).map((_, i) => {
                  const hex = { Czerwony: "#ef4444", Zielony: "#22c55e", Niebieski: "#3b82f6", "Żółty": "#eab308", Fioletowy: "#a855f7", "Biały": "#f8fafc", "Pomarańczowy": "#f97316", Turkusowy: "#14b8a6", Czarny: "#1e293b", "Brązowy": "#92400e", "Różowy": "#ec4899", Szary: "#94a3b8" };
                  const val = (form.tube_colors && form.tube_colors[i]) || fiberColorName(settings, form, i + 1);
                  return (
                    <div key={i} className="flex items-center gap-1.5 min-w-0">
                      <span className="text-[11px] text-slate-400 w-14 shrink-0">{t("Tuba")} {i + 1}</span>
                      <span className="w-3.5 h-3.5 rounded-full border border-slate-600 shrink-0" style={{ background: hex[val] || "#64748b" }} />
                      <select data-testid={`cable-tube-color-${i + 1}`} value={val} onChange={(e) => {
                        const arr = Array.from({ length: tubeCount }).map((__, k) => (form.tube_colors && form.tube_colors[k]) || fiberColorName(settings, form, k + 1));
                        arr[i] = e.target.value; set("tube_colors", arr);
                      }} className="flex-1 min-w-0 bg-slate-900 border border-slate-700 rounded px-1.5 py-1 text-xs">
                        {FIBER_COLORS.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {moduleKey === "closures" && initial?.id && (
            <div className="col-span-2">
              <button type="button" data-testid="closure-splice-pdf"
                onClick={() => window.open(`${API}/closures/${initial.id}/splice-pdf?auth=${token}`, "_blank")}
                className="inline-flex items-center gap-2 px-3 py-2 rounded bg-cyan-700 hover:bg-cyan-600 text-white text-sm">
                <FileText size={15} /> {t("Rozpływ włókien (PDF)")}
              </button>
            </div>
          )}
          {moduleKey === "closures" && initial?.id && (
            <div className="col-span-2">
              <button type="button" data-testid="closure-attach-cables"
                onClick={() => setAttachData({ id: initial.id, number: initial.number, candidates: null, saved: initial })}
                className="inline-flex items-center gap-2 px-3 py-2 rounded bg-slate-700 hover:bg-slate-600 text-white text-sm">
                <Cable size={15} /> {t("Dołącz / odłącz kable")}
              </button>
            </div>
          )}
          {moduleKey === "closures" && initial?.id && (
            <div className="col-span-2"><SpliceMap closureId={initial.id} closureNumber={initial.number} /></div>
          )}
          {moduleKey === "odf" && (form.camera_url || form.rtsp_url) && (
            <div className="col-span-2" data-testid="camera-preview">
              <div className="text-xs text-cyan-400 font-semibold uppercase tracking-wide mb-1">{t("Podgląd kamery IP (na żywo)")}</div>
              <CameraView item={form} className="w-full rounded border border-slate-700 bg-black" style={{ maxHeight: 320, objectFit: "contain" }} testid="camera-preview-view" />
              <p className="text-[11px] text-slate-500 mt-1">{t("Obsługa MJPEG / HTTP snapshot (obraz), HLS/MP4 (wideo) oraz RTSP na żywo przez bramkę serwera (wymaga ffmpeg + zapisanej szafy).")}</p>
            </div>
          )}
          {moduleKey === "routes" && initial?.id && (
            <div className="col-span-2">
              <button type="button" data-testid="route-splice-pdf"
                onClick={() => window.open(`${API}/routes/${initial.id}/splice-pdf?auth=${token}`, "_blank")}
                className="inline-flex items-center gap-2 px-3 py-2 rounded bg-cyan-700 hover:bg-cyan-600 text-white text-sm">
                <FileText size={15} /> {t("Rozpływ włókien muf na trasie (PDF)")}
              </button>
            </div>
          )}
          {(moduleKey === "wells" || moduleKey === "closures") && initial?.id && passing && (
            <div className="col-span-2 border-t border-slate-700 pt-3 mt-1" data-testid="passing-through">
              <span className="text-xs text-cyan-400 font-semibold uppercase tracking-wide">{t("Co przez to przechodzi")}</span>
              {(!passing.cables?.length && !passing.lines?.length) ? (
                <p className="text-[11px] text-slate-500 mt-1">{t("Brak kabli/tras w pobliżu (dodaj geometrię obiektów na mapie).")}</p>
              ) : (
                <div className="mt-1.5 space-y-1 text-xs">
                  {(passing.lines || []).map((l) => (
                    <div key={l.module + l.id} className="flex items-center gap-2 text-slate-300" data-testid={`passing-line-${l.id}`}>
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: MODULES[l.module]?.marker }} />
                      {t(MODULES[l.module]?.singular || l.module)}: {l.name}{l.tube_count ? ` · ${l.tube_count} ${t("mikrorur")}` : ""}
                    </div>
                  ))}
                  {(passing.cables || []).map((c) => (
                    <div key={c.id} className="flex items-center gap-2 text-slate-300" data-testid={`passing-cable-${c.id}`}>
                      <span className="w-2 h-2 rounded-full shrink-0 bg-blue-500" />
                      {t("Kabel")} {c.number} · {c.free}/{c.fiber_count} {t("wolnych włókien")}{c.microduct_tube ? ` · ${t("mikrorura")} ${c.microduct_tube}` : ""}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {moduleKey === "cables" && Number(form.length_m) > 0 && (Number(form.atten_1310) > 0 || Number(form.atten_1550) > 0) && (
            <div className="col-span-2 rounded-md bg-blue-900/20 border border-blue-800/40 px-3 py-2 text-xs" data-testid="cable-attenuation-estimate">
              <div className="text-cyan-400 font-semibold mb-1">Szacowane tłumienie kabla — długość {Number(form.length_m)} m</div>
              <div className="flex gap-4 font-mono">
                <span>1310 nm: <b className="text-slate-100">{((Number(form.length_m) / 1000) * Number(form.atten_1310 || 0)).toFixed(2)} dB</b></span>
                <span>1550 nm: <b className="text-slate-100">{((Number(form.length_m) / 1000) * Number(form.atten_1550 || 0)).toFixed(2)} dB</b></span>
              </div>
            </div>
          )}
          {isCatalog && (
            <div className="col-span-2 border-t border-slate-700 pt-3 mt-1" data-testid="catalog-image-section">
              <span className="text-xs text-cyan-400 font-semibold uppercase tracking-wide">Zdjęcie poglądowe</span>
              <label className="mt-1.5 flex items-center gap-2 bg-slate-900 border border-dashed border-slate-600 rounded px-3 py-2.5 cursor-pointer hover:border-cyan-500">
                <ImagePlus size={16} className="text-cyan-400" />
                <span className="text-sm text-slate-300">{imageFile ? imageFile.name : (initial?.image_file_id ? "Zmień zdjęcie" : "Dodaj zdjęcie poglądowe")}</span>
                <input type="file" accept="image/*" className="hidden" data-testid="catalog-image-input"
                  onChange={(e) => setImageFile(e.target.files?.[0] || null)} />
              </label>
              {(imageFile || initial?.image_file_id) && (
                <img alt="podgląd" data-testid="catalog-image-preview"
                  src={imageFile ? URL.createObjectURL(imageFile) : `${API}/files/${initial.image_file_id}?auth=${token}`}
                  className="mt-2 w-full h-32 object-cover rounded border border-slate-700" />
              )}
            </div>
          )}
          {initial?.properties && Object.keys(initial.properties).length > 0 && (
            <div className="col-span-2 mt-1 rounded-md border border-slate-700 bg-slate-800/40" data-testid="import-props">
              <div className="px-3 py-2 text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-700">{t("Dane z importu")} ({Object.keys(initial.properties).length})</div>
              <div className="max-h-56 overflow-y-auto divide-y divide-slate-800">
                {Object.entries(initial.properties).map(([k, v]) => (
                  <div key={k} className="flex text-xs px-3 py-1.5 gap-2" data-testid={`import-prop-${k}`}>
                    <span className="text-slate-400 w-40 shrink-0 truncate" title={k}>{k}</span>
                    <span className="text-slate-200 break-all">{v === null || v === "" ? "—" : String(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {isPoint && (
            <GpsFields form={form} set={set} outOfBounds={outOfBounds} useMyLocation={useMyLocation} mapCenter={mapCenter} />
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-700 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded border border-slate-600 hover:bg-slate-700">{t("Anuluj")}</button>
          <button onClick={save} disabled={busy} data-testid="record-form-save"
            className="px-4 py-2 text-sm rounded bg-blue-600 hover:bg-blue-500 flex items-center gap-2 disabled:opacity-60">
            <Save size={15} /> {t("Zapisz")}
          </button>
        </div>
      </div>
      {attachData && (
        <AttachCablesDialog closureId={attachData.id} closureNumber={attachData.number} initialCandidates={attachData.candidates}
          closureLat={attachData.saved?.lat} closureLng={attachData.saved?.lng}
          onClose={() => { const s = attachData.saved; const isNew = !initial?.id; setAttachData(null); if (isNew) onSaved(s); }}
          onDone={() => { const s = attachData.saved; const isNew = !initial?.id; setAttachData(null); if (isNew) onSaved(s); }} />
      )}
    </div>
  );
}
